import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../components/common/Icon';
import Button from '../../components/common/Button';
import WhatsAppMessageModal from '../../components/common/WhatsAppMessageModal';
import CollapsibleFilterPanel, { FilterField, filterInputProps } from '../../components/common/CollapsibleFilterPanel';
import { productAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { formatFeedbackLabel } from '../../utils/whatsappMessage';
import { buildOrderFormLink, copyOrderFormLink } from '../../utils/orderFormLink';
import { countActiveDealFilters, filterDeals, formatDateTimeIST } from '../../utils/dealFilters';

const extractDriveFileId = (value) => {
  if (!value) return '';
  try {
    const url = new URL(value);
    if (!url.hostname.includes('drive.google.com')) return '';
    const filePathMatch = url.pathname.match(/\/file\/d\/([^/]+)/);
    return filePathMatch?.[1] || url.searchParams.get('id') || '';
  } catch (error) {
    return '';
  }
};

const resolveImageUrl = (value) => {
  if (!value) return '';
  const fileId = extractDriveFileId(value);
  if (fileId) return `https://drive.google.com/thumbnail?id=${fileId}&sz=w1000`;
  return value;
};

const resolveImageDownloadUrl = (value) => {
  if (!value) return '';
  const fileId = extractDriveFileId(value);
  if (fileId) return `https://drive.google.com/uc?export=download&id=${fileId}`;
  return value;
};

const actionButtonStyle = {
  height: '24px',
  padding: '0 8px',
  borderRadius: '999px',
  border: '1px solid #e2e8f0',
  background: '#ffffff',
  fontSize: '10px',
  fontWeight: '600',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  whiteSpace: 'nowrap'
};

const groupDealsByProduct = (dealsData) =>
  dealsData
    .filter((deal) => deal.active !== false && Number(deal.formsFilledByMediator ?? 0) > 0)
    .map((deal) => ({
      productId: deal.productId || deal.id,
      productName: deal.productName,
      productCode: deal.productCode,
      asinNumber: deal.asinNumber,
      productImageUrl: deal.imageUrl,
      link: deal.link,
      mediatorName: deal.mediatorName,
      active: deal.active,
      formsFilledByMediator: deal.formsFilledByMediator ?? 0,
      platformName: deal.platformName,
      brandName: deal.brandName,
      productAmount: deal.productAmount,
      dealOrderType: deal.dealOrderType,
      creationDate: deal.creationDate,
      feedbacks: (deal.feedbacks || []).map((feedback) => ({
        feedbackType: feedback.feedbackType,
        imageUrl: feedback.imageUrl || '',
        payoutType: feedback.payoutType || 'LESS',
        payoutValue: feedback.payoutValue ?? 0,
        remainingSlots: feedback.remainingSlots ?? 0,
        totalSlots: feedback.totalSlots ?? 0
      }))
    }));

const orderFormBtnStyle = (variant = 'copy', copied = false) => {
  if (variant === 'copy') {
    return {
      padding: '8px 12px',
      background: copied ? '#ecfdf5' : '#eff6ff',
      color: copied ? '#059669' : '#2563eb',
      border: `1px solid ${copied ? '#bbf7d0' : '#bfdbfe'}`,
      borderRadius: '8px',
      fontSize: '12px',
      fontWeight: '700',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '6px',
      whiteSpace: 'nowrap',
      flex: 1,
      minWidth: 0,
    };
  }
  return {
    padding: '8px 12px',
    background: '#f8fafc',
    color: '#475569',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: '700',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    whiteSpace: 'nowrap',
    flex: 1,
    minWidth: 0,
  };
};

const stickyActionsHeader = {
  padding: '16px',
  textAlign: 'left',
  fontSize: '12px',
  fontWeight: '600',
  color: '#64748b',
  textTransform: 'uppercase',
  position: 'sticky',
  right: 0,
  background: '#f8fafc',
  zIndex: 3,
  minWidth: '260px',
  boxShadow: '-4px 0 8px rgba(0, 0, 0, 0.04)',
};

const stickyActionsCell = {
  padding: '16px',
  verticalAlign: 'top',
  position: 'sticky',
  right: 0,
  background: '#ffffff',
  zIndex: 2,
  minWidth: '260px',
  boxShadow: '-4px 0 8px rgba(0, 0, 0, 0.04)',
};

const renderDealActions = (deal, products, copiedOrderFormId, copiedId, handlers) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '228px' }}>
    <button
      type="button"
      onClick={() => handlers.openMessage(products.find((p) => p.productId === deal.productId), 'order')}
      style={{ padding: '8px 12px', background: '#ecfdf5', color: '#059669', border: '1px solid #bbf7d0', borderRadius: '8px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px', whiteSpace: 'nowrap', width: '100%' }}
    >
      <Icon name="message-circle" size={14} /> Order Message
    </button>
    <button
      type="button"
      onClick={() => handlers.openMessage(products.find((p) => p.productId === deal.productId), 'announcement')}
      style={{ padding: '8px 12px', background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a', borderRadius: '8px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px', whiteSpace: 'nowrap', width: '100%' }}
    >
      <Icon name="deals" size={14} /> Deal Announcement
    </button>
    <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
      <button type="button" onClick={() => handlers.copyOrderForm(deal.productId)} style={orderFormBtnStyle('copy', copiedOrderFormId === deal.productId)}>
        <Icon name={copiedOrderFormId === deal.productId ? 'tick' : 'cp'} size={14} />
        {copiedOrderFormId === deal.productId ? 'Copied!' : 'Copy Form Link'}
      </button>
      <button type="button" onClick={() => handlers.openOrderForm(deal.productId)} style={orderFormBtnStyle('open')}>
        <Icon name="external-link" size={14} /> Open Form
      </button>
    </div>
    {deal.link && (
      <button
        type="button"
        onClick={() => handlers.copyLink(deal.link, deal.productId)}
        style={{ ...orderFormBtnStyle('copy', copiedId === deal.productId), width: '100%', flex: 'unset', background: copiedId === deal.productId ? '#ecfdf5' : '#f5f3ff', color: copiedId === deal.productId ? '#059669' : '#7c3aed', border: `1px solid ${copiedId === deal.productId ? '#bbf7d0' : '#ddd6fe'}` }}
      >
        <Icon name={copiedId === deal.productId ? 'tick' : 'cp'} size={14} />
        {copiedId === deal.productId ? 'Copied!' : 'Copy Product Link'}
      </button>
    )}
  </div>
);

const MyDeals = () => {
  const { mediatorId } = useAuth();
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    productCode: '',
    productName: '',
    brandName: '',
    creationDate: '',
  });
  const [copiedId, setCopiedId] = useState(null);
  const [copiedOrderFormId, setCopiedOrderFormId] = useState(null);
  const [messageProduct, setMessageProduct] = useState(null);
  const [messageFeedbacks, setMessageFeedbacks] = useState([]);
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [messageType, setMessageType] = useState('order');
  const [orderFormLink, setOrderFormLink] = useState('');

  const resolveMediatorId = (medId) =>
    (medId && typeof medId !== 'object') ? medId : (mediatorId || JSON.parse(localStorage.getItem('mediatorData') || '{}')?.id);

  const products = useMemo(() => filterDeals(allProducts, filters), [allProducts, filters]);
  const activeFilterCount = useMemo(() => countActiveDealFilters(filters), [filters]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const resetFilters = () => {
    setFilters({ productCode: '', productName: '', brandName: '', creationDate: '' });
  };

  const flatRows = useMemo(() =>
    products.flatMap((product) =>
      (product.feedbacks.length > 0 ? product.feedbacks : [{}]).map((feedback, index) => ({
        ...product,
        ...feedback,
        rowId: `${product.productId}-${feedback.feedbackType || index}`,
        isFirstRow: index === 0,
        rowSpan: product.feedbacks.length || 1
      }))
    ),
  [products]);

  const openMessageModal = (product, type = 'order') => {
    const medId = resolveMediatorId();
    setMessageProduct(product);
    setMessageFeedbacks(product.feedbacks || []);
    setMessageType(type);
    setOrderFormLink(buildOrderFormLink(product.productId, medId));
    setMessageModalOpen(true);
  };

  const handleCopyOrderFormLink = async (productId) => {
    const medId = resolveMediatorId();
    if (!medId) {
      toast.error('Mediator not found');
      return;
    }
    try {
      await copyOrderFormLink(productId, medId);
      setCopiedOrderFormId(productId);
      toast.success('Order form link copied!');
      setTimeout(() => setCopiedOrderFormId(null), 2000);
    } catch (error) {
      toast.error('Failed to copy order form link');
    }
  };

  const handleOpenOrderForm = (productId) => {
    const medId = resolveMediatorId();
    const link = buildOrderFormLink(productId, medId);
    if (!link) {
      toast.error('Could not build order form link');
      return;
    }
    window.open(link, '_blank', 'noopener,noreferrer');
  };

  const handleCopyLink = async (link, id) => {
    if (!link) {
      toast.error('No link available');
      return;
    }
    try {
      await navigator.clipboard.writeText(link);
      setCopiedId(id);
      toast.success('Link copied!');
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const handleViewImage = (imageUrl) => {
    const resolvedUrl = resolveImageUrl(imageUrl);
    if (!resolvedUrl) {
      toast.error('No image available');
      return;
    }
    window.open(resolvedUrl, '_blank', 'noopener,noreferrer');
  };

  const handleDownloadImage = (imageUrl, fileName) => {
    const resolvedUrl = resolveImageDownloadUrl(imageUrl);
    if (!resolvedUrl) {
      toast.error('No image available');
      return;
    }
    const anchor = document.createElement('a');
    anchor.href = resolvedUrl;
    anchor.download = fileName || 'deal-image';
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  const fetchMyDeals = async (medId = null) => {
    const effectiveMedId = resolveMediatorId(medId);
    if (!effectiveMedId) return;

    setLoading(true);
    try {
      const response = await productAPI.getMediatorAssignedProducts(effectiveMedId);
      let dealsData = [];
      if (response.data?.success && Array.isArray(response.data.data)) {
        dealsData = response.data.data;
      } else if (Array.isArray(response.data?.data)) {
        dealsData = response.data.data;
      } else if (Array.isArray(response.data)) {
        dealsData = response.data;
      }

      setAllProducts(groupDealsByProduct(dealsData));
    } catch (error) {
      console.error('Failed to fetch my deals:', error);
      toast.error('Failed to load your deals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initialMedId = resolveMediatorId();
    if (initialMedId) fetchMyDeals(initialMedId);
  }, []);

  useEffect(() => {
    if (mediatorId) fetchMyDeals(mediatorId);
  }, [mediatorId]);

  useEffect(() => {
    const handleDealsUpdated = () => fetchMyDeals();
    window.addEventListener('mediator-deals-updated', handleDealsUpdated);
    return () => window.removeEventListener('mediator-deals-updated', handleDealsUpdated);
  }, []);

  return (
    <div className="page-container admin-page-shell" style={{ padding: '24px', flex: 1, backgroundColor: '#f8fafc', display: 'flex', flexDirection: 'column', width: '100%', overflow: 'auto' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto', width: '100%' }}>
        <div className="admin-page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: '800', color: '#1e293b', margin: '0 0 4px 0' }}>My Deals</h1>
            <p style={{ fontSize: '14px', color: '#64748b' }}>Products where buyers have submitted order forms under your mediator account.</p>
          </div>
          <Button variant="secondary" onClick={() => fetchMyDeals()} disabled={loading}>
            <Icon name="refresh-cw" size={16} /> Refresh
          </Button>
        </div>

        <CollapsibleFilterPanel
          title="Search My Deals"
          activeCount={activeFilterCount}
          onReset={resetFilters}
          gridColumns={4}
        >
          <FilterField label="Product Code">
            <input {...filterInputProps} value={filters.productCode} onChange={(e) => handleFilterChange('productCode', e.target.value)} placeholder="Search code" />
          </FilterField>
          <FilterField label="Product Name">
            <input {...filterInputProps} value={filters.productName} onChange={(e) => handleFilterChange('productName', e.target.value)} placeholder="Search name" />
          </FilterField>
          <FilterField label="Brand">
            <input {...filterInputProps} value={filters.brandName} onChange={(e) => handleFilterChange('brandName', e.target.value)} placeholder="Search brand" />
          </FilterField>
          <FilterField label="Created Date (IST)">
            <input {...filterInputProps} type="date" value={filters.creationDate} onChange={(e) => handleFilterChange('creationDate', e.target.value)} />
          </FilterField>
        </CollapsibleFilterPanel>

        <div style={{ background: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', marginTop: '20px' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center' }}>Loading your deals...</div>
          ) : products.length === 0 ? (
            <div style={{ padding: '60px', textAlign: 'center' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                <Icon name="package" size={40} color="#e2e8f0" />
                <p style={{ color: '#64748b', fontSize: '16px', fontWeight: '500' }}>No deals yet — orders appear here once a buyer fills a form for your mediator.</p>
                <Button variant="outline" onClick={() => { window.location.href = '/mediator/available-deals'; }}>Browse Available Deals</Button>
              </div>
            </div>
          ) : (
            <>
              <div className="mobile-only-list">
                <div className="mobile-card-list" style={{ padding: '12px' }}>
                  {products.map((product) => (
                    <div key={product.productId} className="mobile-card">
                      <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                        <div style={{ width: '56px', height: '56px', borderRadius: '10px', overflow: 'hidden', border: '1px solid #e2e8f0', background: '#f8fafc', flexShrink: 0 }}>
                          {product.productImageUrl ? <img src={resolveImageUrl(product.productImageUrl)} alt={product.productName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1' }}><Icon name="image" size={14} /></div>}
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontWeight: '700', color: '#1e293b', fontSize: '15px' }}>{product.productName}</div>
                          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>{product.productCode} · {product.platformName}</div>
                          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Created: {formatDateTimeIST(product.creationDate)}</div>
                          <span style={{ display: 'inline-block', marginTop: '6px', padding: '4px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '600', background: product.active ? '#d1fae5' : '#fee2e2', color: product.active ? '#059669' : '#b91c1c' }}>
                            {product.active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                      {(product.feedbacks || []).map((feedback, index) => (
                        <div key={`${product.productId}-${feedback.feedbackType || index}`} className="mobile-card-row" style={{ borderTop: index > 0 ? '1px solid #f1f5f9' : undefined, paddingTop: index > 0 ? '10px' : 0 }}>
                          <div>
                            <div className="mobile-card-label">{formatFeedbackLabel(feedback.feedbackType)}</div>
                            <div className="mobile-card-value" style={{ textAlign: 'left', marginTop: '4px' }}>
                              {feedback.payoutType || 'LESS'} · ₹{feedback.payoutValue ?? 0} · {feedback.remainingSlots ?? 0}/{feedback.totalSlots ?? 0} slots
                            </div>
                          </div>
                        </div>
                      ))}
                      <div className="mobile-card-actions">
                        {renderDealActions(product, products, copiedOrderFormId, copiedId, {
                          openMessage: openMessageModal,
                          copyOrderForm: handleCopyOrderFormLink,
                          openOrderForm: handleOpenOrderForm,
                          copyLink: handleCopyLink,
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="desktop-only-table">
                <p className="mobile-scroll-hint">Swipe horizontally to see all columns</p>
                <div className="table-scroll-container deals-table-mobile-scroll" style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1200px' }}>
              <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <tr>
                  <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Product</th>
                  <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Code</th>
                  <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Created (IST)</th>
                  <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Feedback Type</th>
                  <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Payout</th>
                  <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Forms Filled</th>
                  <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Slots Left</th>
                  <th style={{ padding: '16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Status</th>
                  <th style={stickyActionsHeader}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9} style={{ padding: '40px', textAlign: 'center' }}>Loading your deals...</td></tr>
                ) : flatRows.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ padding: '60px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                        <Icon name="package" size={40} color="#e2e8f0" />
                        <p style={{ color: '#64748b', fontSize: '16px', fontWeight: '500' }}>No deals yet — orders appear here once a buyer fills a form for your mediator.</p>
                        <Button variant="outline" onClick={() => { window.location.href = '/mediator/available-deals'; }}>Browse Available Deals</Button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  flatRows.map((deal) => (
                    <tr key={deal.rowId} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      {deal.isFirstRow && (
                        <td style={{ padding: '16px', verticalAlign: 'top' }} rowSpan={deal.rowSpan}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '8px', flexShrink: 0 }}>
                              <div style={{ width: '56px', height: '56px', borderRadius: '10px', overflow: 'hidden', border: '1px solid #e2e8f0', background: '#f8fafc' }}>
                                {deal.productImageUrl ? <img src={resolveImageUrl(deal.productImageUrl)} alt={deal.productName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1' }}><Icon name="image" size={14} /></div>}
                              </div>
                              {deal.productImageUrl && (
                                <div style={{ display: 'flex', gap: '6px', flexDirection: 'column' }}>
                                  <button onClick={() => handleViewImage(deal.productImageUrl)} style={{ ...actionButtonStyle, color: '#2563eb', borderColor: '#bfdbfe', background: '#eff6ff', justifyContent: 'center' }}>
                                    <Icon name="eye" size={11} /> View
                                  </button>
                                  <button onClick={() => handleDownloadImage(deal.productImageUrl, `${deal.productCode || deal.productName || 'deal-image'}.jpg`)} style={{ ...actionButtonStyle, color: '#475569', background: '#f8fafc', justifyContent: 'center' }}>
                                    <Icon name="download" size={11} /> Download
                                  </button>
                                </div>
                              )}
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontWeight: '700', color: '#1e293b', fontSize: '14px' }}>{deal.productName}</div>
                              {deal.platformName && <div style={{ marginTop: '4px', fontSize: '12px', color: '#64748b' }}>{deal.platformName} · ₹{deal.productAmount ?? '-'}</div>}
                              {deal.brandName && <div style={{ marginTop: '4px', fontSize: '12px', color: '#64748b' }}>{deal.brandName}</div>}
                            </div>
                          </div>
                        </td>
                      )}
                      {deal.isFirstRow && (
                        <td style={{ padding: '16px', color: '#475569', verticalAlign: 'top' }} rowSpan={deal.rowSpan}>
                          <span style={{ fontWeight: '600', color: '#334155' }}>{deal.productCode}</span>
                        </td>
                      )}
                      {deal.isFirstRow && (
                        <td style={{ padding: '16px', color: '#475569', verticalAlign: 'top', fontSize: '13px' }} rowSpan={deal.rowSpan}>
                          {formatDateTimeIST(deal.creationDate)}
                        </td>
                      )}
                      <td style={{ padding: '16px', color: '#475569' }}>{formatFeedbackLabel(deal.feedbackType)}</td>
                      <td style={{ padding: '16px', color: '#475569' }}>{deal.payoutType || 'LESS'} · ₹{deal.payoutValue ?? 0}</td>
                      {deal.isFirstRow && (
                        <td style={{ padding: '16px', color: '#475569', verticalAlign: 'top' }} rowSpan={deal.rowSpan}>{deal.formsFilledByMediator ?? 0}</td>
                      )}
                      <td style={{ padding: '16px' }}><div style={{ fontWeight: '700', color: deal.remainingSlots > 0 ? '#059669' : '#ef4444' }}>{deal.remainingSlots ?? 0}/{deal.totalSlots ?? 0}</div></td>
                      {deal.isFirstRow && (
                        <td style={{ padding: '16px', verticalAlign: 'top' }} rowSpan={deal.rowSpan}>
                          <span style={{ padding: '4px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', background: deal.active ? '#d1fae5' : '#fee2e2', color: deal.active ? '#059669' : '#b91c1c' }}>
                            {deal.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      )}
                      {deal.isFirstRow && (
                        <td style={stickyActionsCell} rowSpan={deal.rowSpan}>
                          {renderDealActions(deal, products, copiedOrderFormId, copiedId, {
                            openMessage: openMessageModal,
                            copyOrderForm: handleCopyOrderFormLink,
                            openOrderForm: handleOpenOrderForm,
                            copyLink: handleCopyLink,
                          })}
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <WhatsAppMessageModal
        isOpen={messageModalOpen}
        onClose={() => setMessageModalOpen(false)}
        product={messageProduct}
        feedbacks={messageFeedbacks}
        orderFormLink={orderFormLink}
        messageType={messageType}
        mediatorName={messageProduct?.mediatorName}
      />
    </div>
  );
};

export default MyDeals;
